import { useState } from "react";
import { Alert, Button, Container, Form, InputGroup, Spinner, Table } from "react-bootstrap";
import { connect } from "react-redux";
import resetRegulation from "../actions/regulation/reset";
import saveRegulation from "../actions/regulation/save";
import setEmotionArousalScore from "../actions/regulation/setEmotionArousalScore";
import setEmotionAttenuation from "../actions/regulation/setEmotionAttenuation";
import setRegulationArousalScores from "../actions/regulation/setArousalScores";
import emotions, { compareEmotions } from "../data/emotions";
import { getRegulationArousalHighScore, getRegulationArousalLowScore, getRegulationArousalMediumScore, getRegulationEmotionArousalScores, getRegulationEmotionAttenuations, getRegulationErrors, isFetchingRegulation, isRegulationDirty } from "../store/regulation";

const mapStateToProps = (state) => {
    return {
        arousalLowScore: getRegulationArousalLowScore(state),
        arousalMediumScore: getRegulationArousalMediumScore(state),
        arousalHighScore: getRegulationArousalHighScore(state),
        emotionArousalScores: getRegulationEmotionArousalScores(state),
        emotionAttenuations: getRegulationEmotionAttenuations(state),
        errors: getRegulationErrors(state),
        isDirty: isRegulationDirty(state),
        isFetching: isFetchingRegulation(state),
    };
};

const mapDispatchToProps = {
    resetRegulation,
    saveRegulation,
    setEmotionArousalScore,
    setEmotionAttenuation,
    setRegulationArousalScores,
};

const parseOptionalNumber = (value) => {
    if (value === '') {
        return null;
    }
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const optionalNumberValue = (value) => value ?? '';

const getEmotionArousalScore = (scores, emotion) => scores.find(score => score.emotion === emotion) || {};
const getEmotionAttenuation = (attenuations, emotion) => attenuations.find(attenuation => attenuation.emotion === emotion) || {};

const EmotionNumberInput = ({ label, value, defaultValue, onChange }) => {
	const hasCustomValue = value != null;

	return (
		<Form.Control
			aria-label={label}
			type="number"
			size="sm"
			step="0.1"
			placeholder={`${defaultValue}`}
			value={optionalNumberValue(value)}
			className={`emotion-default-placeholder ${hasCustomValue ? 'border-primary border-2' : ''}`}
			onChange={e => onChange(parseOptionalNumber(e.target.value))}
		/>
	);
};

const DURATION_UNITS = {
	minutes: { label: 'min', step: 1, hours: 1 / 60 },
	hours: { label: 'h', step: 0.1, hours: 1 },
	days: { label: 'j', step: 0.1, hours: 24 },
};

const roundDuration = (value) => Math.round(value * 1e6) / 1e6;

const EmotionDurationInput = ({ label, value, defaultValue, onChange }) => {
	const [unit, setUnit] = useState('minutes');
	const { step, hours } = DURATION_UNITS[unit];
	const hasCustomValue = value != null;

	return (
		<InputGroup size="sm">
			<Form.Control
				aria-label={label}
				type="number"
				size="sm"
				step={step}
				min="0"
				placeholder={`${roundDuration(defaultValue / hours)}`}
				value={value == null ? '' : roundDuration(value / hours)}
				className={`emotion-default-placeholder ${hasCustomValue ? 'border-primary border-2' : ''}`}
				onChange={e => {
					const parsed = parseOptionalNumber(e.target.value);
					onChange(parsed == null ? null : parsed * hours);
				}}
			/>
			<Form.Select
				aria-label={`${label} unité`}
				size="sm"
				value={unit}
				className="flex-grow-0 w-auto"
				onChange={e => setUnit(e.target.value)}
			>
				{Object.entries(DURATION_UNITS).map(([id, { label: unitLabel }]) => (
					<option key={id} value={id}>{unitLabel}</option>
				))}
			</Form.Select>
		</InputGroup>
	);
};

const SaveControls = ({ isDirty, isFetching, saveRegulation, resetRegulation }) => (
    <div className="d-flex flex-wrap gap-2 justify-content-end mb-3">
        <Button
            type="button"
            variant="success"
            className="position-relative"
            disabled={!isDirty || isFetching}
            onClick={saveRegulation}
        >
            <span className={isFetching ? 'invisible' : ''}>Sauvegarder</span>
            <span className={'position-absolute translate-middle top-50 start-50'}>
                <Spinner
                    animation="border"
                    variant="primary"
                    role="status"
                    size='sm'
                    className={!isFetching ? 'invisible' : ''}
                />
            </span>
        </Button>
        <Button
            type="button"
            variant="danger"
            disabled={!isDirty || isFetching}
            onClick={resetRegulation}
        >
            Annuler
        </Button>
    </div>
);

const ModelEmotionSettings = ({
    arousalLowScore,
    arousalMediumScore,
    arousalHighScore,
    emotionArousalScores,
    emotionAttenuations,
    errors,
    isDirty,
    isFetching,
    resetRegulation,
    saveRegulation,
    setEmotionArousalScore,
    setEmotionAttenuation,
    setRegulationArousalScores,
}) => {
    const sortedEmotions = Object.keys(emotions).sort(compareEmotions);

    return (
        <Container fluid className="py-3 px-4">
            <SaveControls
                isDirty={isDirty}
                isFetching={isFetching}
                saveRegulation={saveRegulation}
                resetRegulation={resetRegulation}
            />
            {Object.entries(errors).map(([key, error]) => (
                <Alert key={key} variant="danger">{error}</Alert>
            ))}
            <div className="row g-3 mb-4">
                <div className="col-12 col-md-4">
                    <Form.Label>Score arousal faible</Form.Label>
                    <Form.Control
                        type="number"
                        size="sm"
                        value={arousalLowScore ?? ''}
                        step="0.1"
                        onChange={e => setRegulationArousalScores(parseOptionalNumber(e.target.value) ?? 0, arousalMediumScore, arousalHighScore)}
                    />
                </div>
                <div className="col-12 col-md-4">
                    <Form.Label>Score arousal moyen</Form.Label>
                    <Form.Control
                        type="number"
                        size="sm"
                        value={arousalMediumScore ?? ''}
                        step="0.1"
                        onChange={e => setRegulationArousalScores(arousalLowScore, parseOptionalNumber(e.target.value) ?? 0, arousalHighScore)}
                    />
                </div>
                <div className="col-12 col-md-4">
                    <Form.Label>Score arousal fort</Form.Label>
                    <Form.Control
                        type="number"
                        size="sm"
                        value={arousalHighScore ?? ''}
                        step="0.1"
                        onChange={e => setRegulationArousalScores(arousalLowScore, arousalMediumScore, parseOptionalNumber(e.target.value) ?? 0)}
                    />
                </div>
            </div>
            <div className="table-responsive">
                <Table className="align-middle mb-0">
                    <thead>
                        <tr>
                            <th>Émotion</th>
                            <th>Faible</th>
                            <th>Moyen</th>
                            <th>Fort</th>
                            <th>Atténuation</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedEmotions.map(emotion => {
                            const score = getEmotionArousalScore(emotionArousalScores, emotion);
                            const attenuation = getEmotionAttenuation(emotionAttenuations, emotion);
                            const emotionConfig = emotions[emotion];

                            return (
                                <tr key={emotion}>
                                    <th scope="row">
                                        <span className="d-inline-flex align-items-center gap-2">
                                            <span
                                                className="rounded-circle d-inline-block"
                                                style={{ width: 10, height: 10, backgroundColor: emotionConfig.color }}
                                            />
                                            {emotionConfig.label}
                                        </span>
                                    </th>
                                    <td>
                                        <EmotionNumberInput
                                            label={`${emotionConfig.label} score faible`}
                                            value={score.lowScore}
                                            defaultValue={arousalLowScore ?? ''}
                                            onChange={value => setEmotionArousalScore(emotion, 'lowScore', value)}
                                        />
                                    </td>
                                    <td>
                                        <EmotionNumberInput
                                            label={`${emotionConfig.label} score moyen`}
                                            value={score.mediumScore}
                                            defaultValue={arousalMediumScore ?? ''}
                                            onChange={value => setEmotionArousalScore(emotion, 'mediumScore', value)}
                                        />
                                    </td>
                                    <td>
                                        <EmotionNumberInput
                                            label={`${emotionConfig.label} score fort`}
                                            value={score.highScore}
                                            defaultValue={arousalHighScore ?? ''}
                                            onChange={value => setEmotionArousalScore(emotion, 'highScore', value)}
                                        />
                                    </td>
                                    <td>
										<EmotionDurationInput
											label={`${emotionConfig.label} atténuation`}
											value={attenuation.hours}
											defaultValue={emotionConfig.attenuationHours}
											onChange={value => setEmotionAttenuation(emotion, value)}
										/>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>
        </Container>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ModelEmotionSettings);
